import type { LocationUrlName } from "../Urls/LocationUrl.Interface";
import type { CallScriptRequirement, CallScriptSnapshot, CallScriptStep } from "./CallScript.Interface";

/**
 * The step library (ENG-3402). Seeded from John's "Proposed Step Library" (Notion, 2026-09-23):
 * 53 steps in nine groups. Every step is an INTENTION the bot adapts, never wording and never a
 * fact — prices, fees, hours and policies always come from the bot's tools. Swivl staff can
 * override any platform step for an org, or add org steps, in the Steps tab (`call_script_step`
 * rows; `resolveStepLibrary` merges them, org row wins per slug).
 */

/** John's nine groups. Order here is the order the pickers and the Steps tab list them in. */
export const CALL_SCRIPT_STEP_GROUPS = [
  { slug: "opening", label: "Opening (any call)" },
  { slug: "sales-discovery", label: "Sales: discovery" },
  { slug: "sales-recommend", label: "Sales: recommend" },
  { slug: "sales-close", label: "Sales: close" },
  { slug: "wrap-up", label: "Wrap-up" },
  { slug: "service", label: "Service" },
  { slug: "payments", label: "Payments" },
  { slug: "move-out-retention", label: "Move-out & retention" },
  { slug: "past-due-outbound", label: "Past-due (outbound)" }
] as const;

export type CallScriptStepGroup = (typeof CALL_SCRIPT_STEP_GROUPS)[number]["slug"];

export function callScriptStepGroupLabel(group: string): string {
  return CALL_SCRIPT_STEP_GROUPS.find((g) => g.slug === group)?.label ?? group;
}

/** Everything a step carries — the platform catalog and org rows share this shape. */
export interface CallScriptLibraryStep {
  /** The step's identity, same role as a routing group's or script's slug. An org row with a platform slug overrides that step. Never changes. */
  slug: string;
  group: CallScriptStepGroup;
  /** Short display name in Studio ("Sales — Quote the price"). */
  label: string;
  /** The sentence the bot follows — one instruction, imperative, every fact from a tool. */
  sentence: string;
  /** What a live agent sees for this step in the Contact Center Script section (one or two plain lines). */
  liveAgentCopy: string;
  /** What the coaching engine judges to mark the step done — one plain instruction to the judge. */
  coachingCompletionCriteria: string;
  /** Whether the step appears in the agents' Script section (shown only after a takeover). */
  showToAgents: boolean;
  /** Suggested requirements when the step is added; the operator can change them per script. */
  defaultRequirements: CallScriptRequirement[];
  /** Whether "must have" requirements make sense on this step (collecting details). Absent = no. */
  requirementsAllowed?: boolean;
  /**
   * The step only makes sense when this page exists for the location (a URL registry name).
   * The server drops the step from the rendered script when the page does not resolve.
   */
  requiresPage?: LocationUrlName;
}

/** A seeded step: the catalog entry plus the one-line reason for its `showToAgents` default (review aid; not stored). */
export type CallScriptSeededStep = CallScriptLibraryStep & { showToAgentsReason: string };

/** How each requirement reads inside a step's "if you do not yet have … ask" clause, and its Studio label. */
export const CALL_SCRIPT_REQUIREMENTS: Record<CallScriptRequirement, { label: string; phrase: string }> = {
  name: { label: "Name", phrase: "their name" },
  phone: { label: "Phone number", phrase: "a phone number" },
  email: { label: "Email address", phrase: "an email address" },
  location: { label: "Facility", phrase: "which facility they mean" },
  "unit-size": { label: "Unit size", phrase: "the size they need" },
  "move-in-date": { label: "Move-in date", phrase: "when they need the space" },
  "what-storing": { label: "What they're storing", phrase: "what they plan to store" },
  "storage-duration": { label: "How long they'll store", phrase: "how long they expect to store" },
  "lead-source": { label: "How they heard about us", phrase: "how they heard about us" },
  "payment-date": { label: "Payment date", phrase: "when they can pay" }
};

export const CALL_SCRIPT_REQUIREMENT_KEYS = Object.keys(CALL_SCRIPT_REQUIREMENTS) as CallScriptRequirement[];

const ON = true;
const OFF = false;

/**
 * The platform step library — John's 53 steps. Slugs that existed before the seed keep their
 * slug (operator scripts reference them): confirm-location, discover-needs, recommend-size,
 * highlight-features, quote-price, mention-discounts, explain-requirements,
 * share-hours-directions, capture-reservation, take-to-rental-page, offer-callback, close.
 *
 * `showToAgents` defaults (Chuck, 2026-09-25): steps a human runs start ON; bot-mechanics steps
 * (send a link, read the account, navigate) start OFF. The reason sits beside each value.
 */
export const CALL_SCRIPT_SEEDED_STEPS: CallScriptSeededStep[] = [
  // ── Opening (any call) ────────────────────────────────────────────────────
  {
    slug: "get-their-name",
    group: "opening",
    label: "Opening — Get their name",
    sentence: "Ask for their name early and use it through the conversation.",
    liveAgentCopy: "Get their name early and use it.",
    coachingCompletionCriteria: "The customer's name has been asked for or stated, and it has been used at least once since.",
    showToAgents: ON,
    showToAgentsReason: "A human opener — the agent does this in their own words.",
    defaultRequirements: ["name"],
    requirementsAllowed: true
  },
  {
    slug: "confirm-callback-number",
    group: "opening",
    label: "Opening — Confirm a callback number",
    sentence: "Confirm the best number to reach them in case the call drops.",
    liveAgentCopy: "Confirm the best number to call back if the line drops.",
    coachingCompletionCriteria:
      "A callback number has been confirmed with the customer (asked and answered, or the customer confirmed the number on file).",
    showToAgents: ON,
    showToAgentsReason: "Agents run this on a live call.",
    defaultRequirements: ["phone"],
    requirementsAllowed: true
  },
  {
    slug: "ask-to-text",
    group: "opening",
    label: "Opening — Ask if it's OK to text",
    sentence: "Ask whether it is OK to text them links and details.",
    liveAgentCopy: "Ask if it's OK to text them links and details.",
    coachingCompletionCriteria: "The customer has been asked whether texting is OK and has answered.",
    showToAgents: ON,
    showToAgentsReason: "Consent is asked by whoever is talking to the customer.",
    defaultRequirements: []
  },
  {
    slug: "confirm-location",
    group: "opening",
    label: "Opening — Confirm the facility",
    sentence: "Confirm which facility they are asking about — search by city, ZIP or address if they have not said.",
    liveAgentCopy: "Confirm which facility they mean.",
    coachingCompletionCriteria: "It is clear which facility the customer means — they named it or picked it, or the agent confirmed it with them.",
    showToAgents: ON,
    showToAgentsReason: "The agent needs the facility settled before anything else.",
    defaultRequirements: [],
    requirementsAllowed: true
  },

  // ── Sales: discovery ──────────────────────────────────────────────────────
  {
    slug: "ask-before-quoting",
    group: "sales-discovery",
    label: "Sales — Ask before quoting",
    sentence: "If they ask for a price with no size, ask what they are storing first; if they name a size, quote it, then ask.",
    liveAgentCopy: "No size yet? Ask what they're storing before you quote. Size given? Quote it, then ask.",
    coachingCompletionCriteria:
      "Before any price was given without a stated size, the customer was asked what they are storing; or a stated size was quoted and then the question was asked.",
    showToAgents: ON,
    showToAgentsReason: "A conversational rule the agent applies in the moment.",
    defaultRequirements: []
  },
  {
    slug: "ask-why-storage",
    group: "sales-discovery",
    label: "Sales — Ask why they need storage",
    sentence: "Ask what is prompting the need for storage.",
    liveAgentCopy: "Ask what's prompting the need for storage (move, renovation, business…).",
    coachingCompletionCriteria: "The customer has been asked what is prompting their need for storage, or has volunteered it.",
    showToAgents: ON,
    showToAgentsReason: "Discovery question a human asks.",
    defaultRequirements: []
  },
  {
    slug: "discover-needs",
    group: "sales-discovery",
    label: "Sales — Ask what they're storing and when",
    sentence: "Ask what they plan to store and when they need the space before recommending anything.",
    liveAgentCopy: "Ask what they'll store and when they need it, before recommending anything.",
    coachingCompletionCriteria: "Both what the customer plans to store and when they need the space have been asked or stated.",
    showToAgents: ON,
    showToAgentsReason: "Discovery question a human asks.",
    // No default must-haves: today's default never blocked on these (run #362 showed the bot re-asking them before booking).
    defaultRequirements: [],
    requirementsAllowed: true
  },
  {
    slug: "ask-how-long",
    group: "sales-discovery",
    label: "Sales — Ask how long they'll store",
    sentence: "Ask how long they expect to store.",
    liveAgentCopy: "Ask how long they expect to store.",
    coachingCompletionCriteria: "The customer has been asked how long they expect to store, or has said so.",
    showToAgents: ON,
    showToAgentsReason: "Discovery question a human asks.",
    defaultRequirements: ["storage-duration"],
    requirementsAllowed: true
  },
  {
    slug: "ask-how-moving",
    group: "sales-discovery",
    label: "Sales — Ask how they're moving",
    sentence: "Ask how they plan to move in, and mention truck rental if the facility offers it per its own data.",
    liveAgentCopy: "Ask how they'll move in; mention truck rental if we offer it.",
    coachingCompletionCriteria: "The customer has been asked how they plan to move their things in.",
    showToAgents: ON,
    showToAgentsReason: "Discovery question a human asks.",
    defaultRequirements: []
  },
  {
    slug: "ask-access-needs",
    group: "sales-discovery",
    label: "Sales — Ask about access needs",
    sentence: "Ask how often they will visit and whether temperature matters for what they are storing.",
    liveAgentCopy: "Ask how often they'll visit and whether temperature matters.",
    coachingCompletionCriteria: "The customer has been asked how often they will visit or whether temperature control matters.",
    showToAgents: ON,
    showToAgentsReason: "Discovery question a human asks.",
    defaultRequirements: []
  },
  {
    slug: "ask-what-matters-most",
    group: "sales-discovery",
    label: "Sales — Ask what matters most",
    sentence: "Ask what matters most to them — price, security, access — and tailor the rest of the conversation to it.",
    liveAgentCopy: "Ask what matters most (price, security, access…) and tailor the rest to it.",
    coachingCompletionCriteria: "The customer has been asked what matters most to them about storage, or has said so.",
    showToAgents: ON,
    showToAgentsReason: "Discovery question a human asks.",
    defaultRequirements: []
  },
  {
    slug: "ask-lead-source",
    group: "sales-discovery",
    label: "Sales — Ask how they heard about us",
    sentence: "Ask how they heard about us.",
    liveAgentCopy: "Ask how they heard about us.",
    coachingCompletionCriteria: "The customer has been asked how they heard about the business, or has said so.",
    showToAgents: ON,
    showToAgentsReason: "A question a human asks; the answer is worth recording.",
    defaultRequirements: ["lead-source"],
    requirementsAllowed: true
  },

  // ── Sales: recommend ──────────────────────────────────────────────────────
  {
    slug: "recommend-size",
    group: "sales-recommend",
    label: "Sales — Recommend a size",
    sentence:
      "Recommend one or two sizes that fit what they described, using live availability, and say why — and say when what they asked for is more than they need.",
    liveAgentCopy: "Recommend a size and say why it fits what they described.",
    coachingCompletionCriteria: "A specific unit size has been recommended to the customer with a reason tied to what they said they are storing.",
    showToAgents: ON,
    showToAgentsReason: "The agent recommends in their own words.",
    defaultRequirements: []
  },
  {
    slug: "highlight-features",
    group: "sales-recommend",
    label: "Sales — Highlight relevant features",
    sentence: "Point out only the unit types and amenities tied to what they said matters, from the location's own data.",
    liveAgentCopy: "Mention only the features tied to what they said matters.",
    coachingCompletionCriteria: "At least one feature or amenity was mentioned that relates to something the customer said matters to them.",
    showToAgents: ON,
    showToAgentsReason: "The agent picks the features that fit the conversation.",
    defaultRequirements: []
  },
  {
    slug: "frame-value",
    group: "sales-recommend",
    label: "Sales — Frame the value",
    sentence: "Say what the price includes before quoting it, from the location's own data.",
    liveAgentCopy: "Say what the price includes before you quote it.",
    coachingCompletionCriteria: "Before or with the price, the customer was told what the price includes (access, security, features).",
    showToAgents: ON,
    showToAgentsReason: "Framing is delivered by the person quoting.",
    defaultRequirements: []
  },
  {
    slug: "quote-price",
    group: "sales-recommend",
    label: "Sales — Quote the price",
    sentence: "Quote the current price for the recommended size from live availability, and a promotion if one applies — never estimate or round.",
    liveAgentCopy: "Quote the live price (and the promo if one applies). Never estimate.",
    coachingCompletionCriteria: "A specific monthly price has been given for a unit size.",
    showToAgents: ON,
    showToAgentsReason: "The agent quotes from the units panel.",
    defaultRequirements: []
  },
  {
    slug: "mention-discounts",
    group: "sales-recommend",
    label: "Sales — Mention discounts",
    sentence: "Mention the discounts on file they may qualify for and what each one requires.",
    liveAgentCopy: "Mention the discounts they may qualify for.",
    coachingCompletionCriteria:
      "At least one discount or special the customer may qualify for has been mentioned, or the customer was told there are none.",
    showToAgents: ON,
    showToAgentsReason: "The agent decides which discounts fit.",
    defaultRequirements: []
  },
  {
    slug: "suggest-protection",
    group: "sales-recommend",
    label: "Sales — Suggest protection",
    sentence: "Ask roughly what their items are worth, then suggest the coverage the location offers per its policy data.",
    liveAgentCopy: "Ask roughly what their items are worth, then suggest coverage.",
    coachingCompletionCriteria: "The customer was asked roughly what their items are worth, or protection coverage was suggested to them.",
    showToAgents: ON,
    showToAgentsReason: "A human sales step.",
    defaultRequirements: []
  },

  // ── Sales: close ──────────────────────────────────────────────────────────
  {
    slug: "check-in-after-quote",
    group: "sales-close",
    label: "Sales — Check in after the quote",
    sentence: "After the quote, ask how it sounds to them.",
    liveAgentCopy: "After the quote, ask how it sounds.",
    coachingCompletionCriteria: "After a price was given, the customer was asked how it sounds or what they think.",
    showToAgents: ON,
    showToAgentsReason: "A human close step.",
    defaultRequirements: []
  },
  {
    slug: "check-readiness",
    group: "sales-close",
    label: "Sales — Check they're ready to rent",
    sentence: "Check that they have an email address and a card handy so they can rent now.",
    liveAgentCopy: "Check they have an email and a card handy to rent now.",
    coachingCompletionCriteria: "The customer was asked whether they have what they need to rent now (an email address and a payment card).",
    showToAgents: ON,
    showToAgentsReason: "A human close step.",
    defaultRequirements: ["email"],
    requirementsAllowed: true
  },
  {
    slug: "take-to-rental-page",
    group: "sales-close",
    label: "Sales — Ask for the rental (rental page)",
    sentence:
      "When they say they are ready to rent or book (a unit pick alone is not that), call navigate_to_page with page rent_unit to take them to the rental page — do not collect reservation details in chat. On a call, offer to text the link instead.",
    liveAgentCopy: "Ask them to rent now and send the rental link.",
    coachingCompletionCriteria: "The customer was asked to rent now and was taken to, or sent, the rental page link.",
    showToAgents: ON,
    showToAgentsReason: "The agent asks for the rental and sends the link from the panel.",
    defaultRequirements: [],
    requiresPage: "rent_unit"
  },
  {
    slug: "handle-hesitation",
    group: "sales-close",
    label: "Sales — Handle hesitation",
    sentence: "If they hesitate, ask what is holding them back and answer it from the location's own data.",
    liveAgentCopy: "If they hesitate, ask what's holding them back and answer it.",
    coachingCompletionCriteria: "When the customer hesitated, they were asked what is holding them back and that concern was addressed.",
    showToAgents: ON,
    showToAgentsReason: "A human close step.",
    defaultRequirements: []
  },
  {
    slug: "create-urgency",
    group: "sales-close",
    label: "Sales — Create urgency (persistent)",
    sentence: "Mention low availability or a time-limited deal only when live availability or the discounts on file show it.",
    liveAgentCopy: "Mention low availability or a time-limited deal, only when the data shows it.",
    coachingCompletionCriteria: "Low availability or a time-limited deal was mentioned to the customer, grounded in the units or discounts shown.",
    showToAgents: ON,
    showToAgentsReason: "A persistent-style close step the agent chooses to use.",
    defaultRequirements: []
  },
  {
    slug: "capture-reservation",
    group: "sales-close",
    label: "Sales — Offer to reserve",
    sentence:
      "Once they pick a size, offer to reserve it or send the reservation link, capturing their move-in date and the details needed to hold it.",
    liveAgentCopy: "Offer to reserve the unit; capture their move-in date and contact details.",
    coachingCompletionCriteria: "The customer was offered a reservation or the reservation link, and their move-in date was asked for or given.",
    showToAgents: ON,
    showToAgentsReason: "The agent offers and captures the details.",
    // Name + phone, as before the seed: a required move-in date made the bot hold the booking for it (run #362).
    defaultRequirements: ["name", "phone"],
    requirementsAllowed: true
  },
  {
    slug: "invite-visit",
    group: "sales-close",
    label: "Sales — Invite a visit",
    sentence: "Invite them to visit the facility and take a preferred time for the team to confirm.",
    liveAgentCopy: "Invite them to visit; take a preferred time for the team to confirm.",
    coachingCompletionCriteria: "The customer was invited to visit and, if interested, a preferred time was asked for.",
    showToAgents: ON,
    showToAgentsReason: "A human close step.",
    defaultRequirements: []
  },
  {
    slug: "send-quote",
    group: "sales-close",
    label: "Sales — Send a quote (soft)",
    sentence: "Offer to text or email a summary of the quote.",
    liveAgentCopy: "Offer to text or email a summary of the quote.",
    coachingCompletionCriteria: "The customer was offered a texted or emailed summary of the quote.",
    showToAgents: ON,
    showToAgentsReason: "The agent offers; sending is one click from the panel.",
    defaultRequirements: [],
    requirementsAllowed: true
  },
  {
    slug: "offer-callback",
    group: "sales-close",
    label: "Sales — Offer a callback (soft)",
    sentence: "If they are not ready to decide, offer a callback from the team instead of pressing.",
    liveAgentCopy: "Not ready? Offer a callback instead of pressing.",
    coachingCompletionCriteria: "A callback from the team was offered to a customer who was not ready to decide.",
    showToAgents: ON,
    showToAgentsReason: "A human close step.",
    defaultRequirements: ["name", "phone"],
    requirementsAllowed: true
  },
  {
    slug: "one-more-try",
    group: "sales-close",
    label: "Sales — One more try (persistent)",
    sentence: "Before ending, make one last offer to rent or reserve.",
    liveAgentCopy: "Before ending, make one last offer to rent or reserve.",
    coachingCompletionCriteria: "Near the end of the conversation, one final offer to rent or reserve was made.",
    showToAgents: ON,
    showToAgentsReason: "A persistent-style close step the agent chooses to use.",
    defaultRequirements: []
  },

  // ── Wrap-up ───────────────────────────────────────────────────────────────
  {
    slug: "explain-requirements",
    group: "wrap-up",
    label: "Wrap-up — Explain rental requirements",
    sentence: "Explain what is needed to rent and what is due at move-in, using the policy lookup — never a total, never from memory.",
    liveAgentCopy: "Explain what's needed to rent and what's due at move-in (no total).",
    coachingCompletionCriteria: "The customer was told what is required to rent or what is due at move-in.",
    showToAgents: ON,
    showToAgentsReason: "The agent explains from the policies panel.",
    defaultRequirements: []
  },
  {
    slug: "suggest-supplies",
    group: "wrap-up",
    label: "Wrap-up — Suggest a lock and supplies",
    sentence: "Suggest a lock and packing supplies if the facility sells them per its own data.",
    liveAgentCopy: "Suggest a lock and supplies if we sell them on site.",
    coachingCompletionCriteria: "A lock or packing supplies were suggested to the customer.",
    showToAgents: ON,
    showToAgentsReason: "A human wrap-up step.",
    defaultRequirements: []
  },
  {
    slug: "move-in-recap",
    group: "wrap-up",
    label: "Wrap-up — Move-in recap",
    sentence: "Recap access hours, the due date and the key rules from the location's own data.",
    liveAgentCopy: "Recap access hours, due date and key rules.",
    coachingCompletionCriteria: "Access hours, the due date, or the key rules were recapped for the customer after they agreed to rent or reserve.",
    showToAgents: ON,
    showToAgentsReason: "The agent recaps from the panels.",
    defaultRequirements: []
  },
  {
    slug: "share-hours-directions",
    group: "wrap-up",
    label: "Wrap-up — Share hours and directions",
    sentence: "Share hours and directions when they are useful, and offer to text the map link.",
    liveAgentCopy: "Share hours and directions; offer to text the map link.",
    coachingCompletionCriteria: "Hours or directions were shared with the customer, or the map link was offered.",
    showToAgents: ON,
    showToAgentsReason: "The agent shares from the facility card.",
    defaultRequirements: []
  },
  {
    slug: "mention-referrals",
    group: "wrap-up",
    label: "Wrap-up — Mention the referral program",
    sentence: "Mention the referral program if the location has one per its own data.",
    liveAgentCopy: "Mention the referral program if we have one.",
    coachingCompletionCriteria: "The referral program was mentioned to the customer.",
    showToAgents: ON,
    showToAgentsReason: "A human wrap-up step.",
    defaultRequirements: []
  },
  {
    slug: "ask-for-review",
    group: "wrap-up",
    label: "Wrap-up — Ask for a review",
    sentence: "Ask whether they are satisfied; only if they say yes, invite them to leave a review.",
    liveAgentCopy: "Ask if they're satisfied; only if yes, invite a review.",
    coachingCompletionCriteria: "The customer was asked whether they are satisfied, and if they said yes, was invited to leave a review.",
    showToAgents: ON,
    showToAgentsReason: "A human wrap-up step.",
    defaultRequirements: []
  },
  {
    slug: "close",
    group: "wrap-up",
    label: "Wrap-up — Close with next steps",
    sentence: "Close by confirming the next steps and what they will receive.",
    liveAgentCopy: "Close by confirming next steps and what they'll receive.",
    coachingCompletionCriteria: "The conversation was closed with a confirmation of the next steps or what the customer will receive.",
    showToAgents: ON,
    showToAgentsReason: "The agent closes in their own words.",
    defaultRequirements: []
  },

  // ── Service ───────────────────────────────────────────────────────────────
  {
    slug: "confirm-resolved",
    group: "service",
    label: "Service — Confirm it's resolved",
    sentence: "Check that the fix actually worked for them (for example, the gate opened) before moving on.",
    liveAgentCopy: "Check the fix actually worked (e.g. the gate opened).",
    coachingCompletionCriteria: "After a fix or answer was given, the customer was asked to confirm it worked.",
    showToAgents: ON,
    showToAgentsReason: "A human service step.",
    defaultRequirements: []
  },
  {
    slug: "send-the-details",
    group: "service",
    label: "Service — Send the details",
    sentence: "Offer to text or email what they asked for.",
    liveAgentCopy: "Offer to text or email what they asked for.",
    coachingCompletionCriteria: "The customer was offered the details they asked for by text or email.",
    showToAgents: ON,
    showToAgentsReason: "The agent offers; sending is one click from the panel.",
    defaultRequirements: []
  },
  {
    slug: "anything-else",
    group: "service",
    label: "Service — Anything else?",
    sentence: "Ask whether they need anything else before closing.",
    liveAgentCopy: "Ask if they need anything else.",
    coachingCompletionCriteria: "The customer was asked whether they need anything else.",
    showToAgents: ON,
    showToAgentsReason: "A human service step.",
    defaultRequirements: []
  },

  // ── Payments ──────────────────────────────────────────────────────────────
  {
    slug: "review-balance",
    group: "payments",
    label: "Payments — Review the balance",
    sentence: "State their balance, account status and next due date from the account tools — never from memory.",
    liveAgentCopy: "Read them the balance, status and next due date from the account.",
    coachingCompletionCriteria: "The customer's balance or next due date was stated from the account.",
    showToAgents: OFF,
    showToAgentsReason: "Bot mechanics — read from the account tools; the panel already shows it.",
    defaultRequirements: []
  },
  {
    slug: "offer-prepay-deal",
    group: "payments",
    label: "Payments — Offer a prepay deal",
    sentence: "Mention prepay discounts if any are on file.",
    liveAgentCopy: "Mention prepay discounts if we have any.",
    coachingCompletionCriteria: "A prepay discount was mentioned to the customer, or the customer was told there is none.",
    showToAgents: ON,
    showToAgentsReason: "A human payments step.",
    defaultRequirements: []
  },
  {
    slug: "offer-autopay",
    group: "payments",
    label: "Payments — Offer autopay",
    sentence: "If they are not enrolled in autopay, offer it and hand the enrollment off to the team.",
    liveAgentCopy: "Not on autopay? Offer it and hand enrollment to the team.",
    coachingCompletionCriteria: "Autopay was offered to a customer who is not enrolled.",
    showToAgents: ON,
    showToAgentsReason: "A human payments step.",
    defaultRequirements: []
  },
  {
    slug: "send-payment-link",
    group: "payments",
    label: "Payments — Send a payment link",
    sentence: "Text them a payment link.",
    liveAgentCopy: "Send the payment link.",
    coachingCompletionCriteria: "A payment link was sent to the customer.",
    showToAgents: OFF,
    showToAgentsReason: "Bot mechanics — one click from the panel, no coaching needed.",
    defaultRequirements: []
  },
  {
    slug: "confirm-next-due-date",
    group: "payments",
    label: "Payments — Confirm the next due date",
    sentence: "Confirm when their next payment is due, from the account tools.",
    liveAgentCopy: "Confirm when the next payment is due.",
    coachingCompletionCriteria: "The next payment due date was confirmed with the customer.",
    showToAgents: ON,
    showToAgentsReason: "A confirmation the agent gives before closing.",
    defaultRequirements: []
  },

  // ── Move-out & retention ──────────────────────────────────────────────────
  {
    slug: "ask-why-leaving",
    group: "move-out-retention",
    label: "Move-out — Ask why they're leaving",
    sentence: "Ask the reason they are moving out.",
    liveAgentCopy: "Ask why they're moving out.",
    coachingCompletionCriteria: "The customer was asked why they are moving out, or has said why.",
    showToAgents: ON,
    showToAgentsReason: "A human retention step.",
    defaultRequirements: []
  },
  {
    slug: "offer-to-stay",
    group: "move-out-retention",
    label: "Move-out — Offer an option to stay",
    sentence: "Offer an option that fits their reason — a smaller unit, a discount on file, or escalating to the team.",
    liveAgentCopy: "Offer something that fits their reason: smaller unit, a discount, or escalate.",
    coachingCompletionCriteria: "An option to stay that matches the customer's stated reason was offered (smaller unit, discount, or escalation).",
    showToAgents: ON,
    showToAgentsReason: "A human retention step.",
    defaultRequirements: []
  },
  {
    slug: "move-out-requirements",
    group: "move-out-retention",
    label: "Move-out — Remind the requirements",
    sentence: "Remind them of the notice period and move-out rules, using the policy lookup — never from memory.",
    liveAgentCopy: "Remind them of the notice period and move-out rules.",
    coachingCompletionCriteria: "The notice period or move-out rules were explained to the customer.",
    showToAgents: ON,
    showToAgentsReason: "The agent explains from the policies panel.",
    defaultRequirements: []
  },
  {
    slug: "explain-rate-change",
    group: "move-out-retention",
    label: "Retention — Explain a rate change",
    sentence: "Explain a rate increase using only what is on file for the account and the location's policies.",
    liveAgentCopy: "Explain the rate change using only what's on file.",
    coachingCompletionCriteria: "The rate change was explained to the customer using what is on file.",
    showToAgents: ON,
    showToAgentsReason: "A human retention step.",
    defaultRequirements: []
  },

  // ── Past-due (outbound) ───────────────────────────────────────────────────
  {
    slug: "state-the-purpose",
    group: "past-due-outbound",
    label: "Past due — State the purpose",
    sentence: "Say who is contacting them and why, before any details.",
    liveAgentCopy: "Say who you are and why you're reaching out, before details.",
    coachingCompletionCriteria: "The conversation opened by stating who is contacting the customer and why.",
    showToAgents: ON,
    showToAgentsReason: "A human outbound opener.",
    defaultRequirements: []
  },
  {
    slug: "share-the-balance",
    group: "past-due-outbound",
    label: "Past due — Share the balance",
    sentence: "State the amount due from the account tools — never from memory.",
    liveAgentCopy: "State the amount due from the account.",
    coachingCompletionCriteria: "The amount due was stated to the customer.",
    showToAgents: OFF,
    showToAgentsReason: "Bot mechanics — read from the account; the panel shows it.",
    defaultRequirements: []
  },
  {
    slug: "explain-consequences",
    group: "past-due-outbound",
    label: "Past due — Explain the consequences",
    sentence: "Explain what happens if the balance stays unpaid, exactly as the policy lookup states it.",
    liveAgentCopy: "Explain what happens if it stays unpaid, as the policy states.",
    coachingCompletionCriteria: "The consequences of non-payment were explained to the customer.",
    showToAgents: ON,
    showToAgentsReason: "The agent explains from the policies panel.",
    defaultRequirements: []
  },
  {
    slug: "ask-to-pay-today",
    group: "past-due-outbound",
    label: "Past due — Ask to pay today",
    sentence: "Offer to text a payment link so they can pay now.",
    liveAgentCopy: "Ask them to pay today; offer to send the payment link.",
    coachingCompletionCriteria: "The customer was asked to pay today or offered a payment link now.",
    showToAgents: ON,
    showToAgentsReason: "The ask is human; sending the link is one click.",
    defaultRequirements: []
  },
  {
    slug: "get-payment-date",
    group: "past-due-outbound",
    label: "Past due — Get a payment date",
    sentence: "If they cannot pay now, ask when they can.",
    liveAgentCopy: "Can't pay now? Ask when they can.",
    coachingCompletionCriteria: "A customer who could not pay now was asked when they can pay, or gave a date.",
    showToAgents: ON,
    showToAgentsReason: "A human collections step (some operators leave it out).",
    defaultRequirements: ["payment-date"],
    requirementsAllowed: true
  },
  {
    slug: "restate-deadline",
    group: "past-due-outbound",
    label: "Past due — Restate the deadline",
    sentence: "Remind them that the deadline still applies, as the policy lookup states it.",
    liveAgentCopy: "Remind them the deadline still applies.",
    coachingCompletionCriteria: "The customer was reminded that the payment deadline still applies.",
    showToAgents: ON,
    showToAgentsReason: "A human collections step.",
    defaultRequirements: []
  }
];

/** The platform step library as the resolver and renderers read it (without the review-only reason). */
export const CALL_SCRIPT_STEP_LIBRARY: CallScriptLibraryStep[] = CALL_SCRIPT_SEEDED_STEPS.map(({ showToAgentsReason: _reason, ...step }) => step);

export function findCallScriptLibraryStep(
  slug: string,
  library: ReadonlyArray<CallScriptLibraryStep> = CALL_SCRIPT_STEP_LIBRARY
): CallScriptLibraryStep | undefined {
  return library.find((step) => step.slug === slug);
}

export const DEFAULT_SALES_CALL_SCRIPT_SLUG = "sales-call";

/** The steps the shipped Sales script uses — a short list (Chuck, 2026-09-25); operators trim or extend it. */
const DEFAULT_SALES_STEP_SLUGS = [
  "confirm-location",
  "discover-needs",
  "recommend-size",
  "highlight-features",
  "quote-price",
  "mention-discounts",
  "explain-requirements",
  "share-hours-directions",
  "capture-reservation",
  "take-to-rental-page",
  "offer-callback",
  "close"
] as const;

/**
 * The one script the platform ships. Every org has it until a scope overrides
 * or removes it. Steps carry the library's default requirements.
 */
export const DEFAULT_SALES_CALL_SCRIPT: CallScriptSnapshot & { slug: string } = {
  slug: DEFAULT_SALES_CALL_SCRIPT_SLUG,
  name: "Sales call",
  whenToUse: "Someone asking about renting a space — sizes, prices, availability, or moving in.",
  steps: DEFAULT_SALES_STEP_SLUGS.map((slug): CallScriptStep => {
    const step = findCallScriptLibraryStep(slug);
    return { slug, requirements: [...(step?.defaultRequirements ?? [])] };
  })
};

/** "Move-out call" → "move-out-call". Stable identity for a script across scopes. */
export function deriveCallScriptSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

/** "Confirm the visit date" → "org-confirm-the-visit-date": an org-added step's slug, prefixed so it can never collide with a platform slug. */
export function deriveCallScriptStepSlug(label: string): string {
  const base = deriveCallScriptSlug(label.replace(/^[^—]*—\s*/, ""));
  return base ? `org-${base}`.slice(0, 64) : "";
}

/** Requirements may sit on steps that collect details. */
export function callScriptStepAllowsRequirements(
  step: { slug: string },
  library: ReadonlyArray<CallScriptLibraryStep> = CALL_SCRIPT_STEP_LIBRARY
): boolean {
  return findCallScriptLibraryStep(step.slug, library)?.requirementsAllowed === true;
}
